import React, { PropsWithChildren, useState, useEffect } from "react";

import useGrpcQuery from "../../../lib/hooks/useGrpcQuery";
import { useDebounce } from "../../../lib/hooks/useDebounce";

import { Button } from "../../shared/Button/Button";
import { HandleLoadingState } from "../../shared/HandleLoadingState/HandleLoadingState";
import { LoadingBox } from "../../shared/LoadingBox/LoadingBox";
import { SearchBox } from "../../shared/SearchBox/SearchBox";
import ModalSidebar from "../../Modals/ModalSidebar/ModalSidebar";
import { MembersList } from "./MembersList";

import {
  ListGroupMembersRequest,
  useGroupsRequests,
} from "../../../api/grpc/permissions/useGroupsRequests";
import {
  ListPermissionGroupMembersResponse,
  PermissionGroupMember,
} from "../../../api/grpc/permissions/permissions";
import { GroupFormik } from "./lib/useGroupFormik";

import "./MembersModal.scss";

interface Member {
  id: string;
  name: string;
  email: string;
  role?: string;
}

interface Props {
  groupId: string;
  formik: GroupFormik;
  onClose: () => void;
}

const PAGE_SIZE = 20;

export const ViewMembersModal = ({
  groupId,
  formik,
  onClose,
}: PropsWithChildren<Props>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [members, setMembers] = useState<Member[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const { listPermissionGroupMembers } = useGroupsRequests();

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, error, loading } = useGrpcQuery<
    ListGroupMembersRequest,
    ListPermissionGroupMembersResponse
  >({
    method: listPermissionGroupMembers,
    variables: {
      id: groupId,
      page: page,
      pageSize: PAGE_SIZE,
      search: debouncedSearchTerm,
    },
  });

  useEffect(() => {
    setPage(1);
    setMembers([]);
    setHasMore(true);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (data?.members) {
      const newMembers: Member[] = data.members.map(
        (member: PermissionGroupMember) => ({
          id: member.id,
          name: `${member.firstName} ${member.lastName}`.trim() || member.email,
          email: member.email,
          role: "Member",
        })
      );

      if (page === 1) {
        setMembers(newMembers);
      } else {
        setMembers((prevMembers) => {
          const existingIds = new Set(prevMembers.map((m) => m.id));
          const uniqueNewMembers = newMembers.filter(
            (m) => !existingIds.has(m.id)
          );
          return [...prevMembers, ...uniqueNewMembers];
        });
      }

      const totalCount = data.count ?? 0;
      setHasMore(page * PAGE_SIZE < totalCount);
    }
  }, [data, page]);

  const filteredMembers = members;

  const loadMoreMembers = () => {
    if (!loading && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handleMemberRemove = (memberId: string) => {
    const currentMembers = formik.values.members || [];

    const currentDeleteMembers = formik.values.deleteMembers || [];
    const updatedDeleteMembers = currentDeleteMembers.includes(memberId)
      ? currentDeleteMembers
      : [...currentDeleteMembers, memberId];

    formik.setFieldValue("deleteMembers", updatedDeleteMembers);

    setMembers((prev) => prev.filter((member) => member.id !== memberId));
  };

  return (
    <ModalSidebar
      isOpen={true}
      onToggle={onClose}
      title="Members"
      headerDescription="View and remove group members"
      className="MembersModal MembersModal--view"
      centered={true}
    >
      <div className="d-flex flex-col gap-1">
        <div className="flex-a-center justify-content-end">
          <SearchBox
            clearSearchValue={() => setSearchTerm("")}
            searchValue={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <HandleLoadingState
          loading={loading && page === 1}
          loadingPlaceholder={<LoadingBox count={3} minHeight={60} />}
        >
          {error ? (
            <div className="text-center py-4">
              <p className="text-muted">
                Error loading members: {error.message}
              </p>
            </div>
          ) : filteredMembers.length === 0 && !loading ? (
            <div className="text-center py-4">
              <p className="text-muted">
                {debouncedSearchTerm
                  ? "No members found matching your search."
                  : "No members in this group."}
              </p>
            </div>
          ) : (
            <>
              <MembersList
                members={filteredMembers}
                mode="view"
                onMemberRemove={handleMemberRemove}
              />

              {loading && page > 1 && <LoadingBox count={3} minHeight={60} />}

              {hasMore && !debouncedSearchTerm && !loading && (
                <div className="text-center">
                  <Button
                    color="primary"
                    title="Load more members"
                    onClick={loadMoreMembers}
                    disabled={loading}
                    className="MembersModal__loadMore"
                  />
                </div>
              )}
            </>
          )}
        </HandleLoadingState>
      </div>
    </ModalSidebar>
  );
};
